// src/components/pages/Appointments.tsx
'use client';

import { useState, useEffect, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { GLASS_CARD } from '@/components/ui/glass';
import Pagination from '@/components/pages/shared/Pagination';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  exportAppointmentsToExcelDynamic,
  exportAppointmentsToPDFDynamic,
} from '@/lib/exportHelpers';

import {
  FileText,
  FileSpreadsheet,
  UserPlus,
  Columns,
  CalendarX,
} from 'lucide-react';

import { getTourStyles } from '@/components/tour/tourStyles';
import Joyride, { Step } from 'react-joyride';
import getAppointmentsTourSteps from '@/components/tour/company/AppointmentsTourSteps';

import { motion, AnimatePresence } from 'framer-motion';

import { useCitasDetallesPorEmpresa } from '@/components/hooks/useCitas';
import { useClientes } from '@/components/hooks/useClientes';

import {
  ESTADOS_CITA,
  getEstadoColor,
  getIconoPago,
} from '@/components/constants/citas';

import { toast } from 'sonner';
import { usePageHelp, useTourVisibility } from '@/components/hooks/usePageHelp';
import { DEFAULT_APPOINTMENT_COLUMNS } from '@/components/types/appointmentColumns';
import type { AppointmentColumn } from '@/components/types/appointmentColumns';
import ColumnsSelectorDialog from '@/components/pages/dialogs/ColumnsSelectorDialog';

// ============================================================
// CONFIGURACIÓN GENERAL
// ============================================================
const LS_PAGE_SIZE_KEY = (idEmpresa: string) =>
  `appointments_page_size_${idEmpresa || 'global'}`;

const LS_COLUMNS_KEY = (idEmpresa: string) =>
  `appointments_columns_${idEmpresa || 'global'}`;

const containerVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

const rowVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, x: 20, transition: { duration: 0.2 } },
};

// Fondo suave según estado (sin bordes de color, el borde base lo pone el Card glass)
const getEstadoCardBg = (estado: string) => {
  const color = getEstadoColor(estado);
  const bgMap: { [key: string]: string } = {
    'bg-yellow-500': 'bg-yellow-50 dark:bg-yellow-950/40',
    'bg-blue-500': 'bg-blue-50 dark:bg-blue-950/40',
    'bg-green-500': 'bg-green-50 dark:bg-green-950/40',
    'bg-red-500': 'bg-red-50 dark:bg-red-950/40',
    'bg-gray-500': 'bg-gray-50 dark:bg-white/5',
    'bg-purple-500': 'bg-purple-50 dark:bg-purple-950/40',
  };

  return bgMap[color] || 'bg-white dark:bg-[#1c1c1e]';
};

// Acento lateral de color según estado, para usar junto al borde base tipo "glass"
const getEstadoBorderAccent = (estado: string) => {
  const color = getEstadoColor(estado);
  const borderMap: { [key: string]: string } = {
    'bg-yellow-500': 'border-l-yellow-500',
    'bg-blue-500': 'border-l-blue-500',
    'bg-green-500': 'border-l-green-500',
    'bg-red-500': 'border-l-red-500',
    'bg-gray-500': 'border-l-gray-400',
    'bg-purple-500': 'border-l-purple-500',
  };

  return borderMap[color] || 'border-l-gray-300 dark:border-l-white/10';
};

const FIXED_WIDTH_COLUMNS = [
  'estado',
  'fecha',
  'hora_inicio',
  'hora_fin',
  'precio',
];

const normalizeSortValue = (v: unknown): string | number => {
  if (v == null) return '';
  if (typeof v === 'number') return v;
  if (typeof v === 'boolean') return v ? 1 : 0;
  if (v instanceof Date) return v.getTime();
  const s = String(v).trim();
  const t = Date.parse(s);
  if (!Number.isNaN(t) && /\d/.test(s)) return t;
  const n = Number(s.replace(',', '.'));
  if (!Number.isNaN(n) && s !== '') return n;
  return s.toLowerCase();
};

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================
const Appointments = () => {
  const { t } = useTranslation();
  const isMobile = window.innerWidth < 640;
  const idEmpresa =
    typeof window === 'undefined'
      ? ''
      : localStorage.getItem('id_empresa') || '';

  const today = new Date();
  const formatDate = (d: Date) => d.toLocaleDateString('en-CA');

  const firstDay = formatDate(
    new Date(today.getFullYear(), today.getMonth(), 1)
  );
  const lastDay = formatDate(
    new Date(today.getFullYear(), today.getMonth() + 1, 0)
  );

  const [startDate, setStartDate] = useState(firstDay);
  const [endDate, setEndDate] = useState(lastDay);
  const [activeStates, setActiveStates] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [addedClients, setAddedClients] = useState<Set<string>>(new Set());

  const [pageSize, setPageSize] = useState<number>(() => {
    if (typeof window === 'undefined') return 10;
    const saved = localStorage.getItem(LS_PAGE_SIZE_KEY(idEmpresa));
    return saved ? Number(saved) : 10;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(LS_PAGE_SIZE_KEY(idEmpresa), String(pageSize));
  }, [pageSize, idEmpresa]);

  const [columns, setColumns] = useState<AppointmentColumn[]>(() => {
    if (typeof window === 'undefined') return DEFAULT_APPOINTMENT_COLUMNS;
    try {
      const stored = localStorage.getItem(LS_COLUMNS_KEY(idEmpresa));
      if (!stored) return DEFAULT_APPOINTMENT_COLUMNS;
      const parsed = JSON.parse(stored) as { id: string; enabled: boolean }[];
      return DEFAULT_APPOINTMENT_COLUMNS.map((defaultCol) => {
        const saved = parsed.find((c) => c.id === defaultCol.id);
        return saved ? { ...defaultCol, enabled: saved.enabled } : defaultCol;
      });
    } catch {
      return DEFAULT_APPOINTMENT_COLUMNS;
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const minimal = columns.map(({ id, enabled }) => ({ id, enabled }));
    localStorage.setItem(LS_COLUMNS_KEY(idEmpresa), JSON.stringify(minimal));
  }, [columns, idEmpresa]);

  const resetColumns = () => {
    setColumns(DEFAULT_APPOINTMENT_COLUMNS);
    localStorage.removeItem(LS_COLUMNS_KEY(idEmpresa));
  };

  const [showColumnsDialog, setShowColumnsDialog] = useState(false);
  const visibleColumns = columns.filter((c) => c.enabled);

  type SortKey = AppointmentColumn['id'];
  const defaultSortKey = (DEFAULT_APPOINTMENT_COLUMNS.find(
    (c) => c.id === 'fecha'
  )?.id ?? DEFAULT_APPOINTMENT_COLUMNS[0]?.id) as SortKey;

  const [sortKey, setSortKey] = useState<SortKey>(defaultSortKey);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortOrder('asc');
    }
  };

  const renderSortIcon = (key: SortKey) =>
    sortKey === key ? (sortOrder === 'asc' ? '▲' : '▼') : null;

  const { appointments, loading } = useCitasDetallesPorEmpresa(
    startDate,
    endDate,
    activeStates
  );

  const { agregarCliente } = useClientes();

  const steps: Step[] = getAppointmentsTourSteps(isMobile);

  const toggleEstado = (estado: string) =>
    setActiveStates((prev) =>
      prev.includes(estado)
        ? prev.filter((e) => e !== estado)
        : [...prev, estado]
    );

  const filtered = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return appointments.filter((appt) =>
      [
        appt.trabajadores ?? '',
        appt.telefono ?? '',
        appt.nombre_servicio ?? '',
        appt.email_contacto ?? '',
        appt.nombre_cliente ?? '',
        appt.tipo_pago ?? '',
      ]
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [appointments, searchTerm]);

  const sorted = useMemo(() => {
    const col = columns.find((c) => c.id === sortKey);
    const effectiveCol =
      col ?? visibleColumns[0] ?? DEFAULT_APPOINTMENT_COLUMNS[0];

    return [...filtered].sort((a, b) => {
      const aRaw =
        (effectiveCol as any)?.exportValue?.(a) ??
        (effectiveCol as any)?.value?.(a) ??
        '';
      const bRaw =
        (effectiveCol as any)?.exportValue?.(b) ??
        (effectiveCol as any)?.value?.(b) ??
        '';

      const aVal = normalizeSortValue(aRaw);
      const bVal = normalizeSortValue(bRaw);

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filtered, columns, sortKey, sortOrder, visibleColumns]);

  const totalPages = Math.ceil(sorted.length / pageSize) || 1;
  const currentData = sorted.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    if (page > totalPages) setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPages]);

  async function handleAgregarCliente(email: string) {
    if (!email) {
      toast.error(t('appointments.errorNoEmail'));
      return;
    }
    await agregarCliente(idEmpresa, email, null);
    setAddedClients((prev) => new Set([...prev, email]));
  }

  const [runTour, setRunTour] = useState(false);
  usePageHelp(() => setRunTour(true), !loading);
  useTourVisibility(runTour);

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <motion.div
      className="space-y-8 min-w-0 pb-28 lg:pb-0"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* HEADER — el botón de ayuda vive ahora arriba a la derecha, fijo
          y global (HelpButton), lanzado desde usePageHelp. */}
      <motion.div variants={itemVariants}>
        <div className="tour-title flex items-center text-sm text-gray-600 dark:text-gray-400">
          <span className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
            {t('appointments.title')}
          </span>
        </div>
      </motion.div>

      {/* TOUR */}
      <motion.div variants={itemVariants}>
        <Joyride
          steps={steps}
          run={runTour}
          continuous
          showSkipButton
          spotlightClicks
          locale={{
            back: t('profiles.tour.back'),
            close: t('profiles.tour.close'),
            last: t('profiles.tour.last'),
            next: t('profiles.tour.next'),
            skip: t('profiles.tour.skip'),
          }}
          callback={({ status }) => {
            if (status === 'finished' || status === 'skipped')
              setRunTour(false);
          }}
          styles={getTourStyles(isMobile)}
        />
      </motion.div>

      {/* FILTROS */}
      <Card className={`rounded-2xl ${GLASS_CARD}`}>
        <CardContent className="p-4 sm:p-6 space-y-4">
          <div className="flex flex-col sm:flex-row justify-between gap-4">
            {/* Fechas */}
            <div className="flex flex-wrap gap-2 items-center tour-date-filters">
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-10 text-sm w-full sm:w-40 min-w-0 appearance-none dark:bg-white/5 dark:border-white/10 dark:text-white"
              />
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="h-10 text-sm w-full sm:w-40 min-w-0 appearance-none dark:bg-white/5 dark:border-white/10 dark:text-white"
              />
            </div>

            {/* Exportaciones */}
            <div className="flex flex-wrap gap-2 tour-export-buttons">
              <Button
                onClick={() => exportAppointmentsToExcelDynamic(sorted, columns)}
                className="w-full sm:w-auto bg-[#103254] hover:bg-[#0d2742] dark:bg-[#103254] dark:hover:bg-[#0d2742] text-white rounded-lg transition-colors"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                {t('appointments.exportExcel')}
              </Button>

              <Button
                onClick={() => exportAppointmentsToPDFDynamic(sorted, columns)}
                className="w-full sm:w-auto bg-[#103254] hover:bg-[#0d2742] dark:bg-[#103254] dark:hover:bg-[#0d2742] text-white rounded-lg transition-colors"
              >
                <FileText className="w-4 h-4 mr-2" />
                {t('appointments.exportPDF')}
              </Button>
            </div>
          </div>

          {/* Búsqueda */}
          <Input
            type="text"
            placeholder={t('appointments.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            className="tour-search-input w-full sm:w-1/2 dark:bg-white/5 dark:border-white/10 dark:text-white"
          />

          {/* ESTADOS */}
          <div className="flex flex-wrap items-center gap-2 tour-state-filters pt-2 border-t border-gray-100 dark:border-white/10">
            {ESTADOS_CITA.map((estado) => (
              <button
                key={estado.estado}
                onClick={() => {
                  toggleEstado(estado.estado);
                  setPage(1);
                }}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border transition-colors ${
                  activeStates.includes(estado.estado)
                    ? 'border-[#103254] dark:border-blue-400 bg-[#103254]/5 dark:bg-blue-400/10 text-[#103254] dark:text-blue-300'
                    : 'border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400'
                }`}
              >
                <span className={`w-2.5 h-2.5 rounded-full ${estado.color}`} />
                <span className="hidden sm:inline">{estado.label}</span>
                <span className="inline sm:hidden">{estado.short}</span>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* TABLA / ESTADOS */}
      <AnimatePresence>
        {loading ? (
          <motion.div
            variants={itemVariants}
            className="flex justify-center items-center h-32"
          >
            <motion.div
              variants={rowVariants}
              className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#103254]"
            />
          </motion.div>
        ) : currentData.length === 0 ? (
          <Card className={`rounded-2xl ${GLASS_CARD}`}>
            <CardContent className="py-16 text-center text-gray-500 dark:text-gray-400">
              <CalendarX className="w-10 h-10 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
              <p className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                {t('appointments.noAppointments')}
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                {t('appointments.noAppointmentsText')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Vista móvil - Tarjetas */}
            <div className="block lg:hidden space-y-4 tour-table-head">
              {currentData.map((appt) => (
                <motion.div
                  key={appt.id_cita}
                  variants={rowVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <Card
                    className={cn(
                      'rounded-xl shadow-sm border border-white/40 dark:border-white/10 border-l-4',
                      getEstadoBorderAccent(appt.estado_cita),
                      getEstadoCardBg(appt.estado_cita)
                    )}
                  >
                    <CardContent className="p-4 space-y-3">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 dark:text-white text-base">
                          {appt.nombre_servicio}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {appt.trabajadores || '-'}
                        </p>
                      </div>

                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-gray-500 dark:text-gray-400">
                            {t('appointments.cardClient')}:
                          </span>
                          <span className="text-gray-800 dark:text-white">
                            {appt.nombre_cliente || '-'}
                          </span>
                        </div>
                        {appt.telefono && (
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-gray-500 dark:text-gray-400">
                              {t('appointments.cardPhone')}:
                            </span>
                            <span className="text-gray-800 dark:text-white">
                              {appt.telefono}
                            </span>
                          </div>
                        )}
                        {appt.email_contacto && (
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-gray-500 dark:text-gray-400">
                              {t('appointments.cardEmail')}:
                            </span>
                            <span className="text-gray-800 dark:text-white truncate">
                              {appt.email_contacto}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-500 dark:text-gray-400 text-xs">
                            {t('appointments.cardDate')}
                          </span>
                          <p className="font-medium text-gray-800 dark:text-white">
                            {appt.fecha_cita}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-500 dark:text-gray-400 text-xs">
                            {t('appointments.cardSchedule')}
                          </span>
                          <p className="font-medium text-gray-800 dark:text-white">
                            {appt.hora_cita?.slice(0, 5)}
                            {appt.hora_fin && ` - ${appt.hora_fin.slice(0, 5)}`}
                          </p>
                        </div>
                      </div>

                      <div className="pt-2 border-t border-gray-200 dark:border-white/10">
                        <span
                          className={`inline-block px-3 py-1 rounded-full text-xs font-semibold text-white dark:text-gray-100 dark:opacity-90 ${getEstadoColor(
                            appt.estado_cita
                          )}`}
                        >
                          {appt.estado_cita}
                        </span>
                      </div>

                      {appt.tipo_pago && (
                        <div className="flex items-center gap-2 text-sm pt-2 border-t border-gray-200 dark:border-white/10">
                          <span className="text-gray-500 dark:text-gray-400">
                            {t('appointments.cardPayment')}:
                          </span>
                          <div className="flex items-center gap-1">
                            <span className="text-xl">
                              {getIconoPago(appt.tipo_pago)}
                            </span>
                            {appt.precio != null && (
                              <span className="text-gray-800 dark:text-white font-medium">
                                {appt.precio.toFixed(2)} €
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {appt.cliente_sugerido &&
                        !addedClients.has(appt.email_contacto ?? '') && (
                          <Button
                            onClick={() =>
                              handleAgregarCliente(appt.email_contacto ?? '')
                            }
                            variant="outline"
                            size="sm"
                            className="w-full text-green-600 dark:text-green-400 border-green-300 dark:border-green-700 hover:bg-green-50 dark:hover:bg-green-900/20 hover:text-green-700 dark:hover:text-green-300"
                          >
                            <UserPlus className="w-4 h-4 mr-2" />
                            {t('appointments.addClient')}
                          </Button>
                        )}
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Vista desktop - TABLA */}
            <Card
              className={`hidden lg:block rounded-2xl ${GLASS_CARD}`}
            >
              <CardContent className="p-0">
                <div className="overflow-x-auto w-full">
                  <table className="w-full text-sm text-gray-800 dark:text-gray-100">
                    <thead className="tour-table-head bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-300 uppercase text-xs sticky top-0 z-10">
                      <tr>
                        <th
                          className="px-2 py-0 text-center whitespace-nowrap align-middle"
                          style={{ width: 'auto' }}
                        >
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => setShowColumnsDialog(true)}
                            title={t('appointments.colSelectColumns')}
                            className="tour-columns-button hover:bg-gray-200 dark:hover:bg-white/10"
                          >
                            <Columns className="w-4 h-4" />
                          </Button>
                        </th>

                        {visibleColumns.map((col) => (
                          <th
                            key={col.id}
                            onClick={() => handleSort(col.id as SortKey)}
                            className="px-4 py-3 text-left cursor-pointer hover:bg-gray-200 dark:hover:bg-white/10 transition whitespace-nowrap"
                            style={
                              FIXED_WIDTH_COLUMNS.includes(col.id)
                                ? { width: 'auto' }
                                : { minWidth: '150px' }
                            }
                            title={t('appointments.sortBy', { col: col.label })}
                          >
                            {col.label} {renderSortIcon(col.id as SortKey)}
                          </th>
                        ))}

                        <th
                          className="px-4 py-3 text-center whitespace-nowrap"
                          style={{ width: 'auto' }}
                        >
                          {t('appointments.colActions')}
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-100 dark:divide-white/10">
                      {currentData.map((appt, idx) => (
                        <motion.tr
                          key={appt.id_cita}
                          variants={rowVariants}
                          initial="hidden"
                          animate="visible"
                          exit="exit"
                          className={
                            idx % 2 === 0
                              ? 'bg-white dark:bg-transparent'
                              : 'bg-gray-100 dark:bg-white/[0.03]'
                          }
                        >
                          <td className="px-2 py-3 text-center whitespace-nowrap" />

                          {visibleColumns.map((col) => (
                            <td
                              key={col.id}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              {col.render(appt)}
                            </td>
                          ))}

                          <td className="px-4 py-3 text-center whitespace-nowrap">
                            {appt.cliente_sugerido &&
                              !addedClients.has(appt.email_contacto ?? '') && (
                                <button
                                  onClick={() =>
                                    handleAgregarCliente(
                                      appt.email_contacto ?? ''
                                    )
                                  }
                                  className="inline-flex items-center justify-center p-1 rounded hover:bg-green-100 dark:hover:bg-green-900/30"
                                >
                                  <UserPlus className="w-4 h-4 text-green-600 dark:text-green-400" />
                                </button>
                              )}
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* PAGINACIÓN */}
            <Pagination
              page={page}
              totalPages={totalPages}
              totalItems={sorted.length}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
              itemLabel="citas"
              className="tour-pagination"
            />

            {/* DIALOG: selección de columnas */}
            <ColumnsSelectorDialog
              open={showColumnsDialog}
              columns={columns}
              onChange={setColumns}
              onClose={() => setShowColumnsDialog(false)}
              onReset={resetColumns}
            />
          </>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Appointments;